import React from 'react';
import Box from '@mui/joy/Box';
import Stack from '@mui/joy/Stack';
import Typography from '@mui/joy/Typography';
import IconButton from '@mui/joy/IconButton';
import AddIcon from '@mui/icons-material/Add';
import Input from '@mui/joy/Input';
import Button from '@mui/joy/Button';
import Table from '@mui/joy/Table';
import Link from '@mui/joy/Link';
import Sheet from '@mui/joy/Sheet';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import {getPages} from '../../common/pages/pages';
import {NotificationService} from '../../common/notification';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import Textarea from '@mui/joy/Textarea';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import {useNavigate} from 'react-router-dom';
import Chip from '@mui/joy/Chip';
import DeleteIcon from '@mui/icons-material/Delete';
import Tooltip from '@mui/joy/Tooltip';
import ConfirmDialog from '../../components/confirmDialog/ConfirmDialog';
import {formatDateTime} from '../../common/dateTime';
import {AccessBlacklistRow, AccessBlacklistType, accessBlacklistTypeLabel} from '../../models/accessBlacklist';
import {accessBlacklistAddApi, accessBlacklistListApi, accessBlacklistRemoveApi} from './accessBlacklistApi';

const PageSize = 30;

export function AccessBlacklistPage() {
    const navigate = useNavigate();
    const [items, setItems] = React.useState<AccessBlacklistRow[]>([]);
    const [page, setPage] = React.useState(1);
    const [pageCount, setPageCount] = React.useState(1);
    const [typeFilter, setTypeFilter] = React.useState<number | null>(null);
    const [valueFilter, setValueFilter] = React.useState('');
    const [loading, setLoading] = React.useState(false);
    const [addOpen, setAddOpen] = React.useState(false);
    const [addType, setAddType] = React.useState<number>(AccessBlacklistType.User);
    const [addValue, setAddValue] = React.useState('');
    const [addReason, setAddReason] = React.useState('');
    const [addExpires, setAddExpires] = React.useState('');
    const [saving, setSaving] = React.useState(false);
    const [deleteId, setDeleteId] = React.useState<number | null>(null);

    const loadData = React.useCallback(async () => {
        setLoading(true);
        try {
            const result = await accessBlacklistListApi(page, PageSize, typeFilter ?? undefined, valueFilter);
            if (result.result && result.value) {
                setItems(result.value.items || []);
                setPageCount(Math.max(1, Math.ceil((result.value.total || 0) / PageSize)));
            } else {
                NotificationService.alertError('Не удалось загрузить черный список');
            }
        } finally {
            setLoading(false);
        }
    }, [page, typeFilter, valueFilter]);

    React.useEffect(() => {
        loadData().catch(console.error);
    }, [page]);

    const applyFilter = React.useCallback(async () => {
        if (page !== 1) {
            setPage(1);
        } else {
            await loadData();
        }
    }, [page, loadData]);

    const handleAdd = React.useCallback(async () => {
        if (saving) {
            return;
        }
        setSaving(true);
        try {
            const res = await accessBlacklistAddApi(
                addType,
                addValue.trim(),
                addReason.trim(),
                addExpires ? new Date(addExpires).toISOString() : null
            );
            if (res.result) {
                setAddOpen(false);
                setAddValue('');
                setAddReason('');
                setAddExpires('');
                NotificationService.alertSuccess('Запись добавлена в черный список');
                await loadData();
            } else {
                NotificationService.alertError('Не удалось добавить в черный список');
            }
        } finally {
            setSaving(false);
        }
    }, [saving, addType, addValue, addReason, addExpires, loadData]);

    const confirmDelete = React.useCallback(async () => {
        if (deleteId == null) {
            return;
        }
        const res = await accessBlacklistRemoveApi(deleteId);
        setDeleteId(null);
        if (res.result) {
            NotificationService.alertSuccess('Запись удалена');
            await loadData();
        } else {
            NotificationService.alertError('Не удалось удалить запись');
        }
    }, [deleteId, loadData]);

    const valueCell = (row: AccessBlacklistRow) =>
        row.type === AccessBlacklistType.User ? (
            <Link underline="hover" onClick={() => navigate(`/app/users/${row.value}`)}>
                {row.value}
            </Link>
        ) : row.value;

    return (
        <Box
            component="main"
            className="MainContent"
            sx={{
                px: { xs: 2, md: 6 },
                pt: {
                    xs: 'calc(12px + var(--Header-height))',
                    sm: 'calc(12px + var(--Header-height))',
                    md: 3,
                },
                pb: { xs: 2, sm: 2, md: 3 },
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minWidth: 0,
                height: '100dvh',
                gap: 1,
            }}
        >
            <Stack spacing={2}>
                <Typography level="h2" component="h1" sx={{ mb: 1 }}>
                    Черный список
                </Typography>
                <Typography level="body-sm" color="neutral">
                    Полностью закрывает доступ в API и ExternalAPI (403). Это не торговые блокировки и не Steam VAC.
                </Typography>
                <Box
                    sx={{
                        display: 'flex',
                        gap: 2,
                        flexWrap: 'wrap',
                        alignItems: 'flex-end',
                        p: 2,
                        bgcolor: 'background.level1',
                        borderRadius: '8px',
                        mb: 2,
                    }}
                >
                    <FormControl size="sm" sx={{ minWidth: 160 }}>
                        <FormLabel>Тип</FormLabel>
                        <Select
                            value={typeFilter ?? 0}
                            onChange={(_, v) => setTypeFilter(!v ? null : Number(v))}
                        >
                            <Option value={0}>Все</Option>
                            <Option value={AccessBlacklistType.User}>Пользователь</Option>
                            <Option value={AccessBlacklistType.Ip}>IP</Option>
                        </Select>
                    </FormControl>
                    <FormControl size="sm" sx={{ minWidth: 220 }}>
                        <FormLabel>SteamId / IP</FormLabel>
                        <Input
                            placeholder="Поиск..."
                            value={valueFilter}
                            onChange={(e) => setValueFilter(e.target.value)}
                        />
                    </FormControl>
                    <Button onClick={() => applyFilter().catch(console.error)} size="sm">Поиск</Button>
                    <Box sx={{ flex: 1 }} />
                    <Button
                        startDecorator={<AddIcon />}
                        color="primary"
                        size="sm"
                        onClick={() => setAddOpen(true)}
                    >
                        Добавить
                    </Button>
                </Box>

                <Sheet variant="outlined" sx={{ display: { xs: 'none', sm: 'block' } }}>
                    <Table>
                        <thead>
                        <tr>
                            <th>Тип</th>
                            <th>Значение</th>
                            <th>Причина</th>
                            <th>Кто добавил</th>
                            <th>Создано</th>
                            <th>Истекает</th>
                            <th>Последняя попытка</th>
                            <th style={{ width: 80 }}>Действия</th>
                        </tr>
                        </thead>
                        <tbody>
                        {items.map((row) => (
                            <tr key={row.id}>
                                <td>
                                    <Chip size="sm" variant="soft" color={row.type === AccessBlacklistType.Ip ? 'warning' : 'danger'}>
                                        {accessBlacklistTypeLabel(row.type)}
                                    </Chip>
                                </td>
                                <td>{valueCell(row)}</td>
                                <td>{row.reason}</td>
                                <td>{row.createdBy}</td>
                                <td>{formatDateTime(row.created)}</td>
                                <td>{row.expiresAt ? formatDateTime(row.expiresAt) : 'бессрочно'}</td>
                                <td>{row.lastAttemptAt ? formatDateTime(row.lastAttemptAt) : '—'}</td>
                                <td>
                                    <Tooltip title="Удалить">
                                        <IconButton
                                            size="sm"
                                            variant="outlined"
                                            color="danger"
                                            disabled={loading}
                                            onClick={() => setDeleteId(row.id)}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Tooltip>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </Table>
                    <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ p: 1 }}>
                        <Button variant="plain" disabled={page <= 1 || loading} onClick={() => setPage(1)}>
                            В начало
                        </Button>
                        <IconButton variant="plain" disabled={page <= 1 || loading} onClick={() => setPage(Math.max(1, page - 1))}>
                            <KeyboardArrowLeftIcon />
                        </IconButton>
                        {getPages(pageCount, page).map((p) => {
                            const isDots = p === '...';
                            const pageNum = isDots ? NaN : Number(p);
                            const isActive = !isDots && pageNum === page;
                            return (
                                <Button
                                    key={p}
                                    variant={isActive ? 'solid' : 'plain'}
                                    disabled={isDots || loading}
                                    onClick={() => { if (!isDots) { setPage(pageNum); } }}
                                >
                                    {p}
                                </Button>
                            );
                        })}
                        <IconButton variant="plain" disabled={page >= pageCount || loading} onClick={() => setPage(Math.min(pageCount, page + 1))}>
                            <KeyboardArrowRightIcon />
                        </IconButton>
                    </Stack>
                </Sheet>

                <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                    {items.map((row) => (
                        <Box
                            key={row.id}
                            sx={{
                                bgcolor: 'background.level1',
                                borderRadius: 8,
                                p: 2,
                                mb: 2,
                                border: '1px solid',
                                borderColor: 'divider',
                            }}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Chip size="sm" variant="soft" color={row.type === AccessBlacklistType.Ip ? 'warning' : 'danger'}>
                                    {accessBlacklistTypeLabel(row.type)}
                                </Chip>
                                <Typography level="body-xs" color="neutral">
                                    {row.lastAttemptAt ? formatDateTime(row.lastAttemptAt) : 'нет попыток'}
                                </Typography>
                            </Box>
                            <Box sx={{ mb: 1 }}>{valueCell(row)}</Box>
                            <Typography level="body-sm">{row.reason}</Typography>
                            <Typography level="body-xs" color="neutral" sx={{ mt: 1 }}>
                                Истекает: {row.expiresAt ? formatDateTime(row.expiresAt) : 'бессрочно'}
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                                <Tooltip title="Удалить">
                                    <IconButton size="sm" variant="outlined" color="danger" onClick={() => setDeleteId(row.id)}>
                                        <DeleteIcon />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Stack>

            <Modal open={addOpen} onClose={() => setAddOpen(false)}>
                <ModalDialog>
                    <Typography level="h4">Добавить в черный список</Typography>
                    <FormControl>
                        <FormLabel>Тип</FormLabel>
                        <Select value={addType} onChange={(_, v) => setAddType(v ?? AccessBlacklistType.User)}>
                            <Option value={AccessBlacklistType.User}>Пользователь</Option>
                            <Option value={AccessBlacklistType.Ip}>IP</Option>
                        </Select>
                    </FormControl>
                    <FormControl>
                        <FormLabel>SteamId / IP</FormLabel>
                        <Input value={addValue} onChange={(e) => setAddValue(e.target.value)} placeholder="7656119... или 1.2.3.4" />
                    </FormControl>
                    <FormControl>
                        <FormLabel>Причина</FormLabel>
                        <Textarea minRows={2} value={addReason} onChange={(e) => setAddReason(e.target.value)} />
                    </FormControl>
                    <FormControl>
                        <FormLabel>Истекает (необязательно)</FormLabel>
                        <Input type="datetime-local" value={addExpires} onChange={(e) => setAddExpires(e.target.value)} />
                    </FormControl>
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button variant="plain" onClick={() => setAddOpen(false)}>Отмена</Button>
                        <Button
                            loading={saving}
                            onClick={() => handleAdd().catch(console.error)}
                            disabled={!addValue.trim() || !addReason.trim()}
                        >
                            Сохранить
                        </Button>
                    </Stack>
                </ModalDialog>
            </Modal>

            <ConfirmDialog
                title="Подтверждение"
                open={deleteId != null}
                setOpen={(open) => { if (!open) setDeleteId(null); }}
                onConfirm={() => { confirmDelete().catch(console.error); }}
            >
                Вы действительно хотите удалить запись из черного списка?
            </ConfirmDialog>
        </Box>
    );
}
